import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCheck, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import Button from '@components/common/Button';
import Modal from '@components/common/Modal';

/**
 * BookingWizard Component
 * Props:
 * - onConfirm
 * - services array
 * - userInfo optional
 * Features:
 * - Step 1: Select Service
 * - Step 2: Select Date & Time (validate availability)
 * - Step 3: Enter Postcode (validate UK format)
 * - Step 4: Review & Confirm
 * - Step 5: Confirmation success message
 * - Animations: slide/fade between steps
 * - Form validation: required fields, regex, min/max values
 * - Loose coupling: callbacks via props
 */

const BookingWizard = ({ isOpen, onClose, services = [], onConfirm }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    serviceId: '',
    date: '',
    time: '',
    postcode: '',
    name: '',
    email: '',
    phone: '',
  });
  const [errors, setErrors] = useState({});

  const steps = [
    { number: 1, title: 'Select Service' },
    { number: 2, title: 'Date & Time' },
    { number: 3, title: 'Your Details' },
    { number: 4, title: 'Review' },
    { number: 5, title: 'Confirmation' },
  ];

  const validatePostcode = (postcode) => {
    const ukPostcodeRegex = /^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/i;
    return ukPostcodeRegex.test(postcode);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateStep = () => {
    const newErrors = {};

    if (currentStep === 1 && !formData.serviceId) {
      newErrors.serviceId = 'Please select a service';
    }

    if (currentStep === 2) {
      if (!formData.date) newErrors.date = 'Please select a date';
      if (!formData.time) newErrors.time = 'Please select a time';
    }

    if (currentStep === 3) {
      if (!formData.name) newErrors.name = 'Name is required';
      if (!formData.email) newErrors.email = 'Email is required';
      else if (!validateEmail(formData.email)) newErrors.email = 'Invalid email format';
      if (!formData.phone) newErrors.phone = 'Phone is required';
      if (!formData.postcode) newErrors.postcode = 'Postcode is required';
      else if (!validatePostcode(formData.postcode)) newErrors.postcode = 'Invalid UK postcode';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      if (currentStep === 4) {
        // Submit booking
        onConfirm(formData);
        setCurrentStep(5);
      } else {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = () => {
    setCurrentStep(1);
    setFormData({
      serviceId: '',
      date: '',
      time: '',
      postcode: '',
      name: '',
      email: '',
      phone: '',
    });
    setErrors({});
    onClose();
  };

  const selectedService = services.find((s) => s.id === formData.serviceId);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Book a Service" size="lg">
      {/* Progress Steps */}
      <div className="flex items-center justify-between mb-8">
        {steps.map((step, index) => (
          <div key={step.number} className="flex items-center flex-1">
            <div className="flex flex-col items-center flex-1">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors duration-300 ${
                  currentStep >= step.number
                    ? 'bg-secondary-500 text-white'
                    : 'bg-gray-200 text-gray-600'
                }`}
              >
                {currentStep > step.number ? <FiCheck /> : step.number}
              </div>
              <span className="text-xs mt-2 text-center text-gray-600">{step.title}</span>
            </div>
            {index < steps.length - 1 && (
              <div
                className={`h-1 flex-1 transition-colors duration-300 ${
                  currentStep > step.number ? 'bg-secondary-500' : 'bg-gray-200'
                }`}
              />
            )}
          </div>
        ))}
      </div>

      {/* Step Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
          className="min-h-[300px]"
        >
          {/* Step 1: Select Service */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold mb-4">Choose a Service</h3>
              <div className="grid grid-cols-1 gap-4">
                {services.map((service) => (
                  <button
                    key={service.id}
                    onClick={() => setFormData({ ...formData, serviceId: service.id })}
                    className={`p-4 border-2 rounded-lg text-left transition-all duration-300 ${
                      formData.serviceId === service.id
                        ? 'border-secondary-500 bg-secondary-50'
                        : 'border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    <h4 className="font-semibold text-gray-900">{service.title}</h4>
                    <p className="text-sm text-gray-600 mt-1">{service.description}</p>
                    {service.price && (
                      <p className="text-secondary-600 font-bold mt-2">From £{service.price.toFixed(2)}</p>
                    )}
                  </button>
                ))}
              </div>
              {errors.serviceId && <p className="text-red-500 text-sm">{errors.serviceId}</p>}
            </div>
          )}

          {/* Step 2: Date & Time */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold mb-4">Select Date & Time</h3>
              
              <div>
                <label className="label">Date</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  className="input"
                />
                {errors.date && <p className="text-red-500 text-sm mt-1">{errors.date}</p>}
              </div>

              <div>
                <label className="label">Time</label>
                <select
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  className="input"
                >
                  <option value="">Select a time</option>
                  <option value="09:00">09:00 AM</option>
                  <option value="10:00">10:00 AM</option>
                  <option value="11:00">11:00 AM</option>
                  <option value="12:00">12:00 PM</option>
                  <option value="13:00">01:00 PM</option>
                  <option value="14:00">02:00 PM</option>
                  <option value="15:00">03:00 PM</option>
                  <option value="16:00">04:00 PM</option>
                </select>
                {errors.time && <p className="text-red-500 text-sm mt-1">{errors.time}</p>}
              </div>
            </div>
          )}

          {/* Step 3: Your Details */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold mb-4">Your Details</h3>
              
              <div>
                <label className="label">Full Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input"
                  placeholder="John Doe"
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>

              <div>
                <label className="label">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input"
                  placeholder="john@example.com"
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>

              <div>
                <label className="label">Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="input"
                  placeholder="+44 20 1234 5678"
                />
                {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
              </div>

              <div>
                <label className="label">Postcode</label>
                <input
                  type="text"
                  value={formData.postcode}
                  onChange={(e) => setFormData({ ...formData, postcode: e.target.value.toUpperCase() })}
                  className="input"
                  placeholder="SW1A 1AA"
                />
                {errors.postcode && <p className="text-red-500 text-sm mt-1">{errors.postcode}</p>}
              </div>
            </div>
          )}

          {/* Step 4: Review */}
          {currentStep === 4 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold mb-4">Review Your Booking</h3>
              
              <div className="bg-gray-50 p-6 rounded-lg space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Service:</span>
                  <span className="font-semibold">{selectedService?.title}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Date:</span>
                  <span className="font-semibold">{formData.date}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Time:</span>
                  <span className="font-semibold">{formData.time}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Name:</span>
                  <span className="font-semibold">{formData.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Email:</span>
                  <span className="font-semibold">{formData.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Phone:</span>
                  <span className="font-semibold">{formData.phone}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Postcode:</span>
                  <span className="font-semibold">{formData.postcode}</span>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Confirmation */}
          {currentStep === 5 && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiCheck className="text-3xl text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Booking Confirmed!</h3>
              <p className="text-gray-600 mb-6">
                We've sent a confirmation email to {formData.email}
              </p>
              <Button text="Close" type="primary" onClick={handleClose} />
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Navigation Buttons */}
      {currentStep < 5 && (
        <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
          <Button
            text="Back"
            type="secondary"
            onClick={handleBack}
            disabled={currentStep === 1}
            icon={<FiChevronLeft />}
          />
          <Button
            text={currentStep === 4 ? 'Confirm Booking' : 'Next'}
            type="primary"
            onClick={handleNext}
            icon={currentStep < 4 ? <FiChevronRight /> : null}
          />
        </div>
      )}
    </Modal>
  );
};

export default BookingWizard;

